import { useEffect, useRef, useState } from "react";
import { Send, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";

const fmt = (iso) => { try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

const ConvThread = ({ conv, onDeleted }) => {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const endRef = useRef(null);

  useEffect(() => {
    let alive = true;
    const load = async () => { try { const { data } = await api.get(`/admin/conversations/${conv.id}/messages`); if (alive && Array.isArray(data)) setMsgs(data); } catch { /* ignore */ } };
    load();
    const t = setInterval(load, 5000);
    return () => { alive = false; clearInterval(t); };
  }, [conv.id]);

  useEffect(() => { endRef.current?.scrollIntoView({ block: "end" }); }, [msgs.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try { const { data } = await api.post(`/admin/conversations/${conv.id}/messages`, { text }); setMsgs((m) => [...m, data]); setText(""); } catch (x) { toast.error(errMsg(x)); }
  };
  const del = async () => {
    if (!window.confirm("Eliminare questa conversazione?")) return;
    try { await api.delete(`/admin/conversations/${conv.id}`); onDeleted(); } catch (x) { toast.error(errMsg(x)); }
  };

  return (
    <div className="flex h-[560px] flex-col" data-testid="admin-chat-thread">
      <div className="flex items-center justify-between border-b border-[var(--ink)]/10 p-4">
        <div><p className="font-display text-xl">{conv.name}</p>{conv.contact && <p className="text-sm text-[var(--ink-soft)]">{conv.contact}</p>}</div>
        <button data-testid="admin-chat-delete" onClick={del} aria-label="Elimina" className="grid h-9 w-9 place-items-center rounded-full text-red-700 hover:bg-red-50"><Trash2 size={16} /></button>
      </div>
      <div className="flex-1 space-y-3 overflow-y-auto p-4">
        {msgs.map((m) => (
          <div key={m.id} className={`flex ${m.sender === "admin" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm ${m.sender === "admin" ? "bg-[var(--ink)] text-[var(--cream)]" : "bg-white"}`}>
              <p className="whitespace-pre-wrap">{m.text}</p>
              <p className="mt-1 text-[11px] opacity-70">{fmt(m.created_at)}</p>
            </div>
          </div>
        ))}
        <div ref={endRef} />
      </div>
      <form onSubmit={send} className="flex gap-2 border-t border-[var(--ink)]/10 p-3">
        <input data-testid="admin-chat-reply-input" value={text} onChange={(e) => setText(e.target.value)} maxLength={1000} placeholder="Rispondi..." className="flex-1 rounded-full border border-[var(--ink)]/15 bg-white px-4 py-2.5 text-sm outline-none focus:border-[var(--gold)]" />
        <button data-testid="admin-chat-reply-send" aria-label="Invia" className="grid h-11 w-11 place-items-center rounded-full bg-[var(--gold)] text-[var(--ink)]"><Send size={16} /></button>
      </form>
    </div>
  );
};

export const AdminChat = () => {
  const [convs, setConvs] = useState([]);
  const [sel, setSel] = useState(null);

  const load = async () => { try { const { data } = await api.get("/admin/conversations"); setConvs(Array.isArray(data) ? data : []); } catch { /* ignore */ } };
  useEffect(() => {
    load();
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, []);

  const current = convs.find((c) => c.id === sel);
  return (
    <div className="grid overflow-hidden rounded-[2rem] border border-[var(--gold)]/25 bg-white/50 md:grid-cols-[320px_1fr]">
      <div className={`max-h-[560px] overflow-y-auto border-[var(--ink)]/10 md:border-r ${current ? "hidden md:block" : ""}`} data-testid="admin-chat-list">
        {convs.length === 0 && <p className="p-6 text-sm text-[var(--ink-soft)]">Nessun messaggio ancora.</p>}
        {convs.map((c) => (
          <button key={c.id} data-testid={`admin-conv-${c.id}`} onClick={() => { setSel(c.id); setConvs((cs) => cs.map((x) => (x.id === c.id ? { ...x, unread_admin: 0 } : x))); }} className={`block w-full border-b border-[var(--ink)]/5 p-4 text-left transition-colors hover:bg-white ${sel === c.id ? "bg-white" : ""}`}>
            <div className="flex items-center justify-between gap-2">
              <p className="truncate font-medium">{c.name}</p>
              {c.unread_admin > 0 && <span data-testid={`admin-conv-unread-${c.id}`} className="rounded-full bg-[var(--gold)] px-2 py-0.5 text-[11px] text-[var(--ink)]">{c.unread_admin}</span>}
            </div>
            <p className="mt-1 truncate text-sm text-[var(--ink-soft)]">{c.last_message}</p>
            <p className="mt-1 text-[11px] text-[var(--ink-soft)]">{fmt(c.updated_at)}</p>
          </button>
        ))}
      </div>
      <div className={current ? "" : "hidden md:block"}>
        {current ? (
          <>
            <button onClick={() => setSel(null)} className="px-4 pt-3 text-sm text-[var(--ink-soft)] md:hidden">← Tutte le conversazioni</button>
            <ConvThread key={current.id} conv={current} onDeleted={() => { setSel(null); load(); }} />
          </>
        ) : <p className="grid h-[560px] place-items-center text-sm text-[var(--ink-soft)]">Seleziona una conversazione</p>}
      </div>
    </div>
  );
};
